import { useState } from "react";
import type { ReactNode } from "react";
import { LegalReader } from "./LegalReader";
import { Button } from "@/components/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { gateDecision, rowStatus } from "./status";
import { isStaff } from "@/types";
import type { LegalDocumentStatus, LegalStatus } from "@/types";

/**
 * The door.
 *
 * An owner signing in for the first time reads and accepts the terms and the
 * privacy notice before Backstage shows them anything else — a whole screen,
 * not a banner, because a banner is what people learn to scroll past. The
 * same screen comes back when a document's text changes under them. The two
 * honest responses are to read or to leave; there is no third button.
 *
 * A teacher who arrives before the owner has done this sees a different
 * screen: whose move it is, and that nothing is wrong with their account.
 *
 * Nothing is rendered until the file has been read. A dashboard that shows
 * for a second before the door closes is a door with a gap in it.
 */
export function LegalGate({ children }: { children: ReactNode }) {
  const t = useT();
  const { user, institution, signOut } = useAuth();
  /*
    Staff only, and only once their printed password has been replaced. The
    backend refuses every call from an account still holding a temporary
    password, so asking it about the legal file would show a "could not check"
    screen where the route guard's redirect to the password page belongs.
  */
  const asking = user !== undefined && isStaff(user.role) && !user.mustChangePassword;
  const status = useCallable<LegalStatus>(CALLABLES.getLegalStatus, undefined, asking);
  const [reading, setReading] = useState<LegalDocumentStatus | undefined>(undefined);

  // A player never signs in here and a Spontar admin has nothing to accept.
  if (!user || !asking) return <>{children}</>;

  if (status.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        {t("common.loading")}
      </div>
    );
  }

  if (status.failed || status.data === undefined) {
    /*
      Neither open nor shut on a guess. Letting an owner through on a network
      error would make the door depend on the weather; locking a whole
      institution out for the same reason would be worse. Saying what
      happened and offering the two ways forward is the honest screen.
    */
    return (
      <Screen>
        <h1 className="page-title">{t("legal.gate.unavailableTitle")}</h1>
        <p className="mt-2 text-[14px] text-ink-muted">{t("legal.gate.unavailableBody")}</p>
        <div className="mt-8 flex items-center gap-4">
          <Button onClick={status.reload}>{t("common.retry")}</Button>
          <SignOutLink onClick={signOut} label={t("legal.blockingSignOut")} />
        </div>
      </Screen>
    );
  }

  const decision = gateDecision(user.role, status.data.documents);
  if (decision.kind === "open") return <>{children}</>;

  if (decision.kind === "teacher") {
    return (
      <Screen>
        <h1 className="page-title">{t("legal.gate.teacherTitle")}</h1>
        <p className="mt-2 text-[14px] text-ink-muted">
          {t("legal.gate.teacherBody", { institution: institution?.name ?? "" })}
        </p>
        <ul className="mt-6 list-disc pl-5 text-ink">
          {decision.documents.map((document) => (
            <li key={document.id}>{document.title}</li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-ink-muted">{t("legal.gate.teacherNext")}</p>
        <div className="mt-10">
          <SignOutLink onClick={signOut} label={t("legal.blockingSignOut")} />
        </div>
      </Screen>
    );
  }

  // A first sign-in and a changed text can meet on one screen; the heading
  // follows the first sign-in when there is one, because "new version" would
  // be a strange first sentence from a product somebody has never used.
  const firstRun = decision.documents.some((document) => rowStatus(document) === "never");

  return (
    <Screen>
      <h1 className="page-title">
        {t(firstRun ? "legal.gate.firstRunTitle" : "legal.blockingTitle")}
      </h1>
      <p className="mt-2 text-[14px] text-ink-muted">
        {firstRun
          ? t("legal.gate.firstRunBody", { institution: institution?.name ?? "" })
          : t("legal.blockingBody")}
      </p>

      <ul className="mt-8 flex flex-col gap-3">
        {decision.documents.map((document) => (
          <li
            key={document.id}
            className="card flex items-center justify-between gap-4 px-5 py-4"
          >
            <div>
              <p className="font-medium text-ink">{document.title}</p>
              <p className="text-sm text-ink-muted">
                {rowStatus(document) === "newVersion"
                  ? t("legal.acceptedVersion", { version: document.acceptedVersion })
                  : t("legal.status.never")}
              </p>
            </div>
            <Button onClick={() => setReading(document)}>{t("legal.gate.readAndAccept")}</Button>
          </li>
        ))}
      </ul>

      {reading ? (
        <div className="card mt-8 p-5">
          <LegalReader
            document={reading}
            institutionName={institution?.name ?? ""}
            onAccepted={() => {
              setReading(undefined);
              status.reload();
            }}
            onClose={() => setReading(undefined)}
          />
        </div>
      ) : null}

      <p className="mt-8 text-sm text-ink-muted">{t("legal.gate.ownerOnly")}</p>
      <div className="mt-4">
        <SignOutLink onClick={signOut} label={t("legal.blockingSignOut")} />
      </div>
    </Screen>
  );
}

/** The whole viewport, no navigation: the one thing on screen is the door. */
function Screen({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-canvas px-4 py-16">
      <div className="mx-auto max-w-2xl">{children}</div>
    </div>
  );
}

function SignOutLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink"
    >
      {label}
    </button>
  );
}
